import React, { useState } from "react";
import {
  Box,
  Typography,
  Chip,
  IconButton,
  Fab,
  Zoom,
  useTheme,
  useMediaQuery,
  Grid,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

import NavigationIcon from "@mui/icons-material/Navigation";

const locations = [
  "Italy",
  "Dubai",
  "London",
  "Berlin",
  "Rome",
  "Paris",
  "Barcelona",
  "India",
  "China",
  "Japan",
];

export default function GalleryShowcase({ galleryData, onImageSelect }) {
  const [current, setCurrent] = useState(0);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const handleNext = () => {
    setCurrent((prev) => (prev + 1) % galleryData.length);
  };

  const handlePrev = () => {
    setCurrent((prev) => (prev - 1 + galleryData.length) % galleryData.length);
  };

  const handleImageClick = (index) => {
    if (onImageSelect) {
      onImageSelect(galleryData[index]);
    }
  };

  // Get indices for the carousel items
  const getItemIndices = () => {
    if (isMobile) {
      // For mobile, show only the center image
      return [current];
    }

    // For desktop, show 5 images: two before, current, two after
    return [
      (current - 2 + galleryData.length) % galleryData.length,
      (current - 1 + galleryData.length) % galleryData.length,
      current,
      (current + 1) % galleryData.length,
      (current + 2) % galleryData.length,
    ];
  };

  const itemIndices = getItemIndices();

  return (
    <Box
      sx={{
        py: 6,
        textAlign: "center",
        px: 2,
      }}
    >
      <Typography
        variant="h3"
        fontWeight="bold"
        sx={{
          fontFamily: "Montserrat, sans-serif",
          mb: 1,
          color: "#2c3e50",
        }}
      >
        GALLERY
      </Typography>

      <Typography
        variant="h4"
        sx={{
          color: "text.secondary",
          fontFamily: "Montserrat, sans-serif",
          mb: 2,
          fontWeight: 300,
        }}
      >
        My Visual Diary
      </Typography>

      <Typography
        variant="h6"
        sx={{
          color: "text.secondary",
          fontFamily: "Montserrat, sans-serif",
          mb: 4,
          maxWidth: 600,
          mx: "auto",
        }}
      >
        See the world through my home
      </Typography>
{/* 
      <Typography
        variant="body1"
        sx={{
          color: "text.secondary",
          fontFamily: "Montserrat, sans-serif",
          mb: 4,
          fontStyle: "italic",
        }}
      >
        adventures in photos and videos
      </Typography> */}

      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "center",
          gap: 1,
          mb: 6,
        }}
      >
        {locations.map((loc, index) => (
          <Chip
            key={index}
            label={loc}
            variant={index === 0 ? "filled" : "outlined"}
            color={index === 0 ? "primary" : "default"}
            sx={{
              fontWeight: 500,
              fontSize: "0.875rem",
              fontFamily: "Montserrat, sans-serif",
              px: 1.5,
            }}
          />
        ))}
        <Chip
          label="View More →"
          variant="outlined"
          color="default"
          clickable
        />
      </Box>

      <Box
        sx={{
          position: "relative",
          maxWidth: isMobile ? 400 : 1200,
          mx: "auto",
          height: isMobile ? 300 : 400,
        }}
      >
        <Box
          sx={{
            position: "relative",
            height: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          {itemIndices.map((imgIndex, i) => {
            const item = galleryData[imgIndex];
            const isCenter = imgIndex === current;

            // Calculate positions based on index
            let transform, zIndex, scale, opacity, width, height;

            if (isMobile) {
              // Mobile view - only show center image
              transform = "translateX(0)";
              zIndex = 10;
              scale = 1;
              opacity = 1;
              width = 300;
              height = 250;
            } else {
              // Desktop view - carousel with 5 images
              const positionIndex = i - 2; // -2, -1, 0, 1, 2

              if (positionIndex === 0) {
                // Center
                transform = "translateX(0)";
                zIndex = 10;
                scale = 1;
                opacity = 1;
                width = 400;
                height = 350;
              } else if (Math.abs(positionIndex) === 1) {
                // Adjacent
                transform = `translateX(${positionIndex * 270}px)`;
                zIndex = 9;
                scale = 0.9;
                opacity = 1;
                width = 350;
                height = 300;
              } else {
                // Outer
                transform = `translateX(${positionIndex * 230}px)`;
                zIndex = 8;
                scale = 0.8;
                opacity = 1;
                width = 300;
                height = 250;
              }
            }

            return (
              <Box
                key={imgIndex}
                sx={{
                  position: "absolute",
                  transform: `${transform} scale(${scale})`,
                  zIndex,
                  opacity,
                  transition: "all 0.5s ease",
                  cursor: "pointer",
                }}
                onClick={() => handleImageClick(imgIndex)}
              >
                <Box
                  sx={{
                    width,
                    height,
                    borderRadius: 2,
                    overflow: "hidden",
                    backgroundColor: "#fff",
                    boxShadow: isCenter
                      ? "0 15px 30px rgba(0,0,0,0.2)"
                      : "0 8px 16px rgba(0,0,0,0.1)",
                    position: "relative",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: isCenter ? "scale(1.02)" : "scale(1.05)",
                    },
                  }}
                >
                  <Box
                    component="img"
                    src={item.image}
                    alt={item.title}
                    sx={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />

                  {/* Center image overlay */}
                  {isCenter && (
                    <Box
                      sx={{
                        position: "absolute",
                        bottom: 0,
                        left: 0,
                        right: 0,
                        background:
                          "linear-gradient(to top, rgba(0,0,0,0.7) 0%, transparent 100%)",
                        color: "white",
                        p: 2,
                      }}
                    >
                      <Typography
                        variant="h6"
                        sx={{ fontWeight: 600, mb: 0.5, fontSize: "1rem" }}
                      >
                        {item.title}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ opacity: 0.9, fontSize: "0.8rem" }}
                      >
                        {item.location} • {item.date}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>
        {/* Navigation Buttons */}
        {/* {!isMobile && (
          <>
            <IconButton
              onClick={handlePrev}
              sx={{
                position: 'absolute',
                top: '50%',
                left: -20,
                transform: 'translateY(-50%)',
                width: 40,
                height: 40,
                borderRadius: '50%',
                backgroundColor: 'white',
                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                zIndex: 15,
                '&:hover': {
                  backgroundColor: '#2563eb',
                  color: 'white',
                },
              }}
            >
              <ChevronLeftIcon />
            </IconButton>

            <IconButton
              onClick={handleNext}
              sx={{
                position: 'absolute',
                top: '50%',
                right: -20,
                transform: 'translateY(-50%)',
                width: 40,
                height: 40,
                borderRadius: '50%',
                backgroundColor: 'white',
                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                zIndex: 15,
                '&:hover': {
                  backgroundColor: '#2563eb',
                  color: 'white',
                },
              }}
            >
              <ChevronRightIcon />
            </IconButton>
          </>
        )} */}
      </Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          gap: "20px", 
        }}
      >
        <IconButton
          sx={{
            border: "2px solid #000", // Customize border
            borderRadius: "50%",
            padding: "10px",
          }}
          onClick={handlePrev}
        >
          <ArrowBackIcon />
        </IconButton>

        <IconButton
          sx={{
            border: "2px solid #000",
            borderRadius: "50%",
            padding: "10px",
          }}
          onClick={handleNext}
        >
          <ArrowForwardIcon />
        </IconButton>
      </Box>
      {/* Floating action button for navigation */}
      <Zoom in={true}>
        <Fab
          variant="extended"
          color="primary"
          sx={{
            position: "fixed",
            bottom: 16,
            right: 16,
          }}
        >
          <NavigationIcon sx={{ mr: 1 }} />
          Explore More
        </Fab>
      </Zoom>
    </Box>
  );
}
